/*
    Copyright (C) 2023 - Wheel and Brake - Southwest Airlines
*/
import { Button, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import FieldValidation from "../commons/FieldValidation";
import WnbModal from "../commons/WnbModalAlert";
import WnbModalSuccess from "../commons/WnbModalSuccess";
import WnbModalError from "../commons/WnbModalError";
import { getAll } from "../../../backend/read/GetAll";
import { create } from "../../../backend/create/Create";
import { update } from "../../../backend/update/Update";
import {ACCESS_TOKEN_KEY} from "../../../utilities/Constants";

const PrinterForm = (props) => {
  const [showAlert, setShowAlert] = useState(false);
  const [id] = useState(props.id);
  const [printerStatus, setPrinterStatus] = useState((id && props.printerStatus) || "active");
  const [stationCode, setStationCode] = useState(props.stationCode || "");
  const [printerName, setPrinterName] = useState(props.printerName || "");
  const [validStationCode, setValidStationCode] = useState(true);
  const [stationCodeValidationMessage, setStationCodeValidationMessage] = useState("");
  const [stationCodeValidationType, setStationCodeValidationType] = useState("");
  const [validPrinterName, setValidPrinterName] = useState(true);
  const [printerNameValidationMessage, setPrinterNameValidationMessage] = useState("");
  const [printerNameValidationType, setPrinterNameValidationType] = useState("");
  const activeTagsCount = (props.activeTagsCount && parseInt(props.activeTagsCount)) || 0;
  const inactiveAlertMessage = "This printer has had " + activeTagsCount + ((activeTagsCount === 1 && " tag") || " tags") + " printed within the last 7 days.  Are you sure you want to INACTIVATE this printer?";
  const [showInactiveAlert, setShowInactiveAlert] = useState(false);
  const [getPrinterValues, setGetPrinterValues] = useState([]);
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [showNotSavedAlert, setShowNotSavedAlert] = useState(false);
  const [mode] = useState(props.mode || "create");
  const saveLabel = "Save";
  const headerLabel = id ? "Edit" : "Create";
  const navigate = useNavigate();
  const [inputDuplicateValue, setInputDuplicateValue] = useState(false);

  const onChangeStationCode = (event) => {
    let newStationCode = event.target.value;
    const attemptExceed = newStationCode.length > 8;
    newStationCode = newStationCode.substring(0, 8);
    setStationCode(newStationCode);
    if (attemptExceed) {
      setValidation(false, "Station Code is not valid. Please create a Station Code with no more than 8 characters.");
    }
  };

  useEffect(() => {
    const getNames = () => {
      getAll("PRINTERS").then(r => {
        JSON.parse(r.body.data).Items.forEach(i => {
          setGetPrinterValues(current => [...current, i.PRINTER_ID.toUpperCase()]);
        });
      })
    };
    getNames();
  }, []);

  const onBlurStationCode = (event) => {
    let newStationCode = event.target.value;
    if (newStationCode.length < 3) {
      setValidation(false, "Station Code is not valid. Please create a Station Code using a minimum of 3 characters.");
    }
  }

  const closeStationValueError = () => {
    setValidation(true);
  }

  const setValidation = (valid, message, type) => {
    setValidStationCode(valid);
    setStationCodeValidationMessage(message || "");
    setStationCodeValidationType(type || "error");
  };

  const setPrinterValidation = (valid, message, type) => {
    setValidPrinterName(valid);
    setPrinterNameValidationMessage(message || "");
    setPrinterNameValidationType(type || "error");
  };

  const onChangePrinterName = (event) => {
    const hasInvalidChar = event.target.value.match(/[^A-Za-z0-9]/gi);
    let newPrinterName = event.target.value.replace(/[^A-Za-z0-9]/gmi, "");
    const attemptExceed = newPrinterName.length > 15;
    newPrinterName = newPrinterName.substring(0, 15);
    setPrinterName(newPrinterName);
    setInputDuplicateValue(false);
    const duplicateName = getPrinterValues.includes(event.target.value.trim().toUpperCase());
    if (hasInvalidChar) {
      setPrinterValidation(false, "Printer Name is not valid.  The Printer Name cannot contain special characters or spaces.");
    } else if (attemptExceed) {
      setPrinterValidation(false, "Printer Name is not valid. Please create a Printer Name that is 15 characters or less.");
    } else if (duplicateName) {
      setInputDuplicateValue(true);
      setPrinterValidation(false, "This Printer Name is already defined.  Please enter a unique Printer Name value.");
    }
  };

  const onChangeStatus = (event) => {
    const newPrinterStatus = event.target.value;
    setPrinterStatus(newPrinterStatus);
    //display warning popup when marking printer as inactive which has active tags
    if (id && newPrinterStatus === "inactive" && activeTagsCount > 0) {
      setShowInactiveAlert(true);
    }
  };

  const onBlurPrinterName = (event) => {
    let newPrinterName = event.target.value;
    if (newPrinterName.length < 3) {
      setPrinterValidation(false, "Printer Name is not valid. Please create a Printer Name using a minimum of 3 characters.");
    }
  };

  const closePrintNameValueError = () => {
    setPrinterValidation(true);
  };


  const onSave = () => {
    if (mode === "create") {
      let body = {
          "PRINTER_ID": {
            "S": printerName.toUpperCase()
          },
          "STATION_CODE": {
            "S": stationCode.toUpperCase()
          },
          "ACTIVE_FLAG": {
            "BOOL": printerStatus === "active"
          },
          "ACCESS_TOKEN": {
              "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
          }
      }
      create("PRINTERS", body).then(resp => {
        if (resp.statusCode === 200) {
          setShowSaveAlert(true);
          setSaveMessage("Printer '" + printerName.toUpperCase() + "' saved successfully.");
        } else {
          setSaveErrorMessage("Printer '" + printerName.toUpperCase() + "' was not created. Please try again.");
          setShowNotSavedAlert(true);
        }
      });
    }
    else {
      let body = {
          "PRINTER_ID": {
            "S": printerName.toUpperCase()
          },
          "STATION_CODE": {
            "S": stationCode.toUpperCase()
          },
          "ACTIVE_FLAG": {
            "BOOL": printerStatus === "active"
          },
          "ACCESS_TOKEN": {
              "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
          }
      }
      update("PRINTERS", body).then(resp => {
        if (resp.statusCode === 200) {
          setShowSaveAlert(true);
          setSaveMessage("Printer '" + printerName.toUpperCase() + "' updated successfully.");
        } else {
          setSaveErrorMessage("Printer '" + printerName.toUpperCase() + "' was not updated. Please try again.");
          setShowNotSavedAlert(true);
        }
      });
    }
  };
  const onCancel = () => {
    setShowAlert(true);
  };

  const onClickModalYes = () => {
    setStationCode(props.stationCode || "");
    setPrinterName(props.printerName || "");
    setPrinterStatus((id && props.printerStatus) || "active");
    closeStationValueError();
    closePrintNameValueError();
    setShowAlert(false);
  };

  const onClickModalNo = () => {
    setShowAlert(false)
  };

  const onClickOkSaveError = () => {
    setShowNotSavedAlert(false);
  };

  const onClickOkSaveSuccess = () => {
    setShowSaveAlert(false);
  };

  const onClickNew = () => {
    if (mode === "update") {
      navigate('/create-printer');
    }
    else {
      window.location.reload();
    }
  };

  const onClickShowList = () => {
    navigate('/printer-list');
  };

  const onClickInactiveModalYes = () => {
    setShowInactiveAlert(false);
  };

  const onClickInactiveModalNo = () => {
    setPrinterStatus("active");
    setShowInactiveAlert(false);
  };

  return (
    <div className="form-main">
      <Form className="create-form">
        <div className="form-header">
          <div>{headerLabel} Printer</div>
          <div className="header-note"><span style={{ color: 'var(--southwest-red)' }}>&#10045;</span> denotes a mandatory field</div>
        </div>
        <Form.Group className="form-status">
          <Form.Check
            name="printStatus"
            type="radio"
            className="wnb-label"
            label="ACTIVE"
            value="active"
            checked={printerStatus === "active"}
            bsPrefix="printstatus-radio"
            onChange={onChangeStatus}
          />
          <Form.Check
            name="printStatus"
            type="radio"
            className="wnb-label"
            label="INACTIVE"
            value="inactive"
            checked={printerStatus === "inactive"}
            onChange={onChangeStatus}
          />
        </Form.Group>
        <Form.Group className="fieldmarginCls">
          <Form.Label className="wnb-label">Station Code<span style={{ color: 'var(--southwest-red)', paddingLeft: '2px' }}>&#10045;</span></Form.Label>
          <div className="field-control">
            <Form.Control
              type="stationCode"
              placeholder="Enter Station Code"
              value={stationCode}
              className={(!validStationCode && 'invalid-field-' + stationCodeValidationType + ' stationInput') || ' stationInput'}
              onChange={onChangeStationCode}
              onBlur={onBlurStationCode}
            />
            <FieldValidation show={!validStationCode} type={stationCodeValidationType} message={stationCodeValidationMessage} onClose={closeStationValueError} />
          </div>
        </Form.Group>
        <Form.Group className="fieldmarginCls">
          <Form.Label className="wnb-label">Printer Name<span style={{ color: 'var(--southwest-red)', paddingLeft: '2px' }}>&#10045;</span></Form.Label>
          <div className="field-control">
            <Form.Control
              type="printerName"
              placeholder="Enter Printer Name"
              value={printerName}
              className={(!validPrinterName && 'invalid-field-' + printerNameValidationType + ' printerInput') || ' printerInput'}
              onChange={onChangePrinterName}
              onBlur={onBlurPrinterName}
              disabled={!!id}
            />
            <FieldValidation show={!validPrinterName} type={printerNameValidationType} message={printerNameValidationMessage} onClose={closePrintNameValueError} />
          </div>
        </Form.Group>
        <Form.Group className="action-panel">
          <Button disabled={!!!stationCode || inputDuplicateValue === true || !validPrinterName || !validStationCode || !!!printerName || stationCode.length < 3 || printerName.length < 3} onClick={onSave} >{saveLabel}</Button>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        </Form.Group>
      </Form>
      <WnbModal
        title="Warning"
        message="Are you sure you wish to cancel?  Clicking YES will reset all data."
        handleCancel={onClickModalNo}
        handleYes={onClickModalYes}
        showAlert={showAlert}
      />
      <WnbModalSuccess
        title="Success"
        message={saveMessage}
        handleOk={onClickOkSaveSuccess}
        handleNew={onClickNew}
        handleShowList={onClickShowList}
        showAlert={showSaveAlert}
      />
      <WnbModalError
        title="Error"
        message={saveErrorMessage}
        handleOk={onClickOkSaveError}
        showAlert={showNotSavedAlert}
      />
      <WnbModal
        title="Warning"
        message={inactiveAlertMessage}
        handleCancel={onClickInactiveModalNo}
        handleYes={onClickInactiveModalYes}
        showAlert={showInactiveAlert}
      />
    </div >
  );
};

export default PrinterForm;
